import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDndContext,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronRight,
  CopyPlus,
  ExternalLink,
  Eye,
  GripVertical,
  Minus,
  MousePointerClick,
  Package,
  Plus,
  QrCode,
  RefreshCw,
  Settings2,
  Trash2,
  X, SlidersHorizontal } from 'lucide-react';
import { allocationsApi } from '@/features/stock/api';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useFormPanel } from '@/components/layout/formPanelStore';
import { GameGlanceChip } from './GameGlanceChip';
import type { DragData, DropData } from './dnd';
import type { XkEventStockStation, XkStation } from '@/lib/api/types';
import { toast } from '@/components/ui/Toast';
import { Tooltip } from '@/components/ui/Tooltip';
import { IconButtonGroup, IconButtonGroupItem } from '@/components/ui/IconButtonGroup';
import { Badge } from '@/components/ui/Badge';
import { AllocationPrizeModeFields } from '@/features/stock/AllocationPrizeModeFields';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

type StockLite = { name: string; image?: string | null; quantityRemaining?: number | null; rating?: number | null };

/** Sort allocation rows by their persisted `position`, falling back to id. */
function byPosition(a: XkEventStockStation, b: XkEventStockStation): number {
  const pa = a.position ?? Number.MAX_SAFE_INTEGER;
  const pb = b.position ?? Number.MAX_SAFE_INTEGER;
  return pa !== pb ? pa - pb : a.id - b.id;
}

/**
 * Expandable station card with an inline, per-station stock-allocation editor.
 *
 * Each allocation row (one wheel slice) is shown individually — the same stock
 * can repeat as several rows — and can be reordered by drag (persists the
 * `position` field), have its quantity tuned, be duplicated into another slice,
 * or removed. A nested DndContext isolates this sortable list from the
 * planner's carousel→event drag.
 */
export function StationCard({
  station,
  eventId,
  brandId,
  companyId,
  isLast,
  allocations,
  stockMap,
}: {
  station: XkStation;
  eventId: number;
  brandId: number;
  companyId: number;
  isLast: boolean;
  allocations: XkEventStockStation[];
  stockMap: Map<number, StockLite>;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const openForm = useFormPanel((s) => s.openForm);
  const [open, setOpen] = useState(false);

  // Drop target (outer planner DndContext): a xotski dropped here is allocated
  // to THIS station only. The nested sortable DndContext below is separate.
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `station-drop-${station.id}`,
    data: { eventId, stationId: station.id } satisfies DropData,
  });
  const activeData = useDndContext().active?.data.current as DragData | undefined;
  const draggingXotski = activeData?.type === 'xotski';

  // Local order for instant drag feedback; resynced when the query refreshes.
  const [rows, setRows] = useState<XkEventStockStation[]>(() =>
    [...allocations].sort(byPosition),
  );
  useEffect(() => {
    setRows([...allocations].sort(byPosition));
  }, [allocations]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byEvent(eventId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byStation(station.id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byBrand(brandId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stock.byBrand(brandId) });
  };

  const removeStation = useMutation({
    mutationFn: () => eventsApi.deleteStation(station.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      toast({ tone: 'success', title: t('planner.events.stationRemoved') });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('planner.events.stationRemoveFailed'), description: e.message }),
  });

  // Persist new positions for every row whose index changed.
  const persistOrder = useMutation({
    mutationFn: (ordered: XkEventStockStation[]) =>
      Promise.all(
        ordered.map((r, i) =>
          (r.position ?? -1) === i ? Promise.resolve(r) : allocationsApi.update(r.id, { position: i }),
        ),
      ),
    onSuccess: invalidate,
    onError: (e: Error) => {
      toast({ tone: 'error', title: t('planner.station.reorderFailed'), description: e.message });
      invalidate();
    },
  });

  const duplicate = useMutation({
    mutationFn: (row: XkEventStockStation) =>
      allocationsApi.create({
        eventId,
        stationId: station.id,
        stockId: row.stockId,
        quantity: 1,
        available: 1,
        companyId,
        position: rows.length,
      }),
    onSuccess: () => {
      invalidate();
      toast({ tone: 'success', title: t('planner.station.duplicated') });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('planner.station.duplicateFailed'), description: e.message }),
  });

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === Number(active.id));
    const newIndex = rows.findIndex((r) => r.id === Number(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(rows, oldIndex, newIndex);
    setRows(next); // optimistic
    persistOrder.mutate(next);
  };

  const totalQty = rows.reduce((s, r) => s + (r.quantity ?? 0), 0);

  return (
    <li
      ref={setDropRef}
      className={cn(
        'relative overflow-hidden rounded-lg border bg-[var(--color-bg-base)] transition-all',
        isOver
          ? 'border-[var(--color-accent-ink)] ring-2 ring-[var(--color-xk-accent)] bg-[var(--color-xk-accent)]/5'
          : draggingXotski
            ? 'border-dashed border-[var(--color-accent-ink)]/60'
            : 'border-[var(--color-border-subtle)]',
      )}
    >
      {/* Drop affordance — shown while a xotski hovers this station. */}
      {isOver && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-[var(--color-xk-accent)]/10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-xk-accent)] px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-on-accent)] shadow-lg">
            <MousePointerClick className="h-3.5 w-3.5" />
            {t('planner.drop.here')}
          </span>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-1.5 py-1.5 pl-1.5 pr-1.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
          aria-label={open ? t('planner.station.collapse') : t('planner.station.expand')}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-surface)]">
          <QrCode className="h-3.5 w-3.5 text-[var(--color-accent-ink)]" />
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="min-w-0 flex-1 text-left"
        >
          <span className="block max-w-full truncate text-[12px] font-semibold text-[var(--color-xk-lavender)]">
            {station.name}
          </span>
          <span className="block text-[10px] text-[var(--color-xk-text-muted)] tabular-nums">
            {t('planner.station.sliceCount', { count: String(rows.length) })} · {totalQty}
          </span>
        </button>
        {/* Task 4.5 — same glance on the station: the journey cascades from the
            project, so the game is the event's. */}
        <GameGlanceChip eventId={eventId} stationId={station.id} />
        {/* Las acciones de la estación en una sola píldora; borrar queda fuera,
            en rojo, para que lo destructivo no vaya pegado a «abrir». */}
        <IconButtonGroup label={t('planner.station.actions')}>
          {/* Refrescar la disponibilidad a mano. Con el evento en marcha, lo que
              queda cambia con cada premio y esta lista no se refresca sola: la
              invalidación sólo llega tras una edición propia. */}
          <IconButtonGroupItem
            label={t('planner.station.refresh')}
            icon={<RefreshCw className="h-3.5 w-3.5" />}
            onClick={() => {
              invalidate();
              toast({ tone: 'success', title: t('planner.station.refreshed') });
            }}
          />
          {rows.length > 0 && (
            <IconButtonGroupItem
              label={t('planner.station.preview')}
              icon={<Eye className="h-3.5 w-3.5" />}
              onClick={() =>
                openForm({
                  kind: 'station-preview',
                  mode: 'create',
                  title: t('planner.station.previewTitle', { name: station.name }),
                  context: { eventId, brandId, stationId: station.id },
                })
              }
            />
          )}
          {/* Task 3.5 — tune this station's xotskies in the right rail. The
              board stays put; no navigation to a separate page. */}
          <IconButtonGroupItem
            label={t('planner.station.editAllocations')}
            icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
            onClick={() =>
              openForm({
                kind: 'station-allocations',
                mode: 'edit',
                title: t('planner.station.allocationsTitle', { name: station.name }),
                context: { stationId: station.id, eventId, brandId, companyId },
              })
            }
          />
          <IconButtonGroupItem
            label={t('planner.events.openStationConfig')}
            icon={<ExternalLink className="h-3.5 w-3.5" />}
            to={`/stations/${station.id}`}
          />
        </IconButtonGroup>
        {/* Un botón deshabilitado no recibe eventos de puntero, así que el
            tooltip («debe quedar al menos una estación») cuelga del span. */}
        <Tooltip description={isLast ? t('planner.events.lastStation') : t('planner.events.removeStation')}>
          <span className="inline-flex">
            <button
              type="button"
              disabled={removeStation.isPending || isLast}
              onClick={() => removeStation.mutate()}
              aria-label={t('planner.events.removeStation')}
              className="grid h-6 w-6 place-items-center rounded-full text-[var(--color-error)] transition-colors hover:bg-[var(--color-error)]/10 disabled:pointer-events-none disabled:opacity-30"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </Tooltip>
      </div>

      {/* Allocation editor */}
      {open && (
        <div className="border-t border-[var(--color-border-subtle)] p-2">
          {rows.length === 0 ? (
            <p className="rounded-md border border-dashed border-[var(--color-xk-border)] px-2 py-2 text-[11px] text-[var(--color-xk-text-muted)]">
              {t('planner.station.empty')}
            </p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                <ul className="flex flex-col gap-1.5">
                  {rows.map((row) => (
                    <AllocationRow
                      key={row.id}
                      row={row}
                      stock={stockMap.get(row.stockId)}
                      eventId={eventId}
                      brandId={brandId}
                      onDuplicate={() => duplicate.mutate(row)}
                      duplicating={duplicate.isPending}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </li>
  );
}

function AllocationRow({
  row,
  stock,
  eventId,
  brandId,
  onDuplicate,
  duplicating,
}: {
  row: XkEventStockStation;
  stock: StockLite | undefined;
  eventId: number;
  brandId: number;
  onDuplicate: () => void;
  duplicating: boolean;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });
  const label = stock?.name ?? `#${row.stockId}`;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byEvent(eventId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stockAllocations.byBrand(brandId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.stock.byBrand(brandId) });
    // The lane reads `remaining` from the project pool for adopted stocks and
    // `allocated` is its floor in the commit dialog: both moved. This card
    // has no groupId, so refresh every project's pool.
    queryClient.invalidateQueries({ queryKey: queryKeys.projectStockPool.all });
  };

  // Quantity edits reset `available` to match — the backend re-validates brand
  // headroom and rejects an over-allocation with a surfaced error.
  /*
   * Cambiar la cantidad mueve la disponibilidad EN LA MISMA MEDIDA, no la
   * reinicia. Antes hacía `available = quantity`: con el evento en marcha,
   * subir de 20 a 25 borraba las 8 unidades ya entregadas y la ruleta volvía a
   * repartir lo que ya no estaba en la caja. Lo asignado y lo que queda son
   * dos números distintos y aquí se tocan a la vez sólo por el delta.
   */
  const setQty = useMutation({
    mutationFn: (quantity: number) => {
      const delta = quantity - (row.quantity ?? 0);
      const available = Math.max(0, Math.min(quantity, (row.available ?? 0) + delta));
      return allocationsApi.update(row.id, { quantity, available });
    },
    onSuccess: invalidate,
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('planner.alloc.qtyFailed'), description: e.message }),
  });
  // Los mandos de premio (093): peso o cadencia, y el «ojo».
  const setPrize = useMutation({
    mutationFn: (patch: Partial<XkEventStockStation>) => allocationsApi.update(row.id, patch),
    onSuccess: invalidate,
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('planner.alloc.saveFailed'), description: e.message }),
  });
  const [ajustes, setAjustes] = useState(false);
  const remove = useMutation({
    mutationFn: () => allocationsApi.remove(row.id),
    onSuccess: () => {
      invalidate();
      toast({ tone: 'success', title: t('planner.alloc.removed') });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('planner.alloc.removeFailed'), description: e.message }),
  });

  const qty = row.quantity ?? 0;
  const disponible = row.available ?? 0;
  const busy = setQty.isPending || remove.isPending || duplicating;

  /**
   * The quantity is typeable, not only steppable — reaching 100 was 100 clicks.
   * `draft` holds what is being typed so the field can be transiently empty or
   * mid-word without firing a request per keystroke; it re-syncs whenever the
   * server value changes (own mutation, or another edit invalidating the list).
   */
  const [draft, setDraft] = useState(String(qty));
  useEffect(() => setDraft(String(qty)), [qty]);

  const commitDraft = () => {
    const parsed = Number.parseInt(draft, 10);
    // Floor of 1 mirrors the minus button: 0 means "remove", which is the trash.
    const next = Number.isFinite(parsed) ? Math.max(1, parsed) : qty;
    setDraft(String(next));
    if (next !== qty) setQty.mutate(next);
  };

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] py-1 pl-1 pr-1.5',
        isDragging && 'z-10 opacity-80 shadow-lg',
        row.hiddenFromGames && 'opacity-70',
      )}
    >
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        className="flex h-5 w-4 shrink-0 cursor-grab items-center justify-center text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)] active:cursor-grabbing"
        aria-label={t('planner.station.drag')}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      {stock?.image ? (
        <img src={resolveImageUrl(stock.image)} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" />
      ) : (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-base)]">
          <Package className="h-3.5 w-3.5 text-[var(--color-xk-text-muted)]" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[11px] font-semibold text-[var(--color-xk-lavender)]">
          {label}
        </span>
        {/* Lo que QUEDA, junto a lo asignado. Es lo que mira quien lleva el
            evento y hasta ahora sólo se veía en la ficha de la estación. */}
        <span className="flex items-center gap-1 text-[10px] tabular-nums text-[var(--color-xk-text-muted)]">
          {t('planner.alloc.availableOf', { available: String(disponible), quantity: String(qty) })}
          {disponible <= 0 ? <Badge tone="warn">{t('planner.alloc.soldOut')}</Badge> : null}
          {row.hiddenFromGames ? <Badge tone="neutral">{t('planner.alloc.hidden')}</Badge> : null}
        </span>
      </span>
      <button
        type="button"
        disabled={busy || qty <= 1}
        onClick={() => setQty.mutate(qty - 1)}
        aria-label={t('planner.alloc.decrease')}
        className="flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-bg-base)] hover:text-[var(--color-xk-lavender)] disabled:opacity-30"
      >
        <Minus className="h-3 w-3" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        // The row is drag-and-drop; without this the pointer-down that focuses
        // the field starts a drag instead of placing the caret.
        onPointerDown={(e) => e.stopPropagation()}
        value={draft}
        disabled={busy}
        onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
        onFocus={(e) => e.target.select()}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setDraft(String(qty));
            e.currentTarget.blur();
          }
        }}
        aria-label={t('planner.alloc.qtyInput')}
        // Grows with the digits so 4-figure allocations don't clip.
        style={{ width: `${Math.max(1.25, draft.length * 0.62 + 0.5)}rem` }}
        className="rounded border border-transparent bg-transparent px-0.5 text-center text-[11px] font-black tabular-nums text-[var(--color-xk-lavender)] hover:border-[var(--color-border-default)] focus:border-[var(--color-accent-ink)] focus:outline-none disabled:opacity-40"
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => setQty.mutate(qty + 1)}
        aria-label={t('planner.alloc.increase')}
        className="flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-bg-base)] hover:text-[var(--color-xk-lavender)] disabled:opacity-30"
      >
        <Plus className="h-3 w-3" />
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onDuplicate}
        aria-label={t('planner.station.duplicate')}
        title={t('planner.station.duplicate')}
        className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-bg-base)] hover:text-[var(--color-accent-ink)] disabled:opacity-30"
      >
        <CopyPlus className="h-3 w-3" />
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => remove.mutate()}
        aria-label={t('planner.alloc.remove')}
        className="flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-error)] transition-colors hover:bg-[rgb(239_68_68/0.12)] disabled:opacity-30"
      >
        <Trash2 className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={() => setAjustes((v) => !v)}
        aria-expanded={ajustes}
        aria-label={t('planner.alloc.settings')}
        title={t('planner.alloc.settings')}
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded-full transition-colors hover:bg-[var(--color-bg-base)]',
          ajustes ? 'text-[var(--color-accent-ink)]' : 'text-[var(--color-xk-text-muted)] hover:text-[var(--color-accent-ink)]',
        )}
      >
        <Settings2 className="h-3 w-3" />
      </button>
    </div>
    {/* Plegado por defecto: la fila sigue siendo la de siempre y los mandos
        sólo ocupan sitio en el regalo que se está ajustando. */}
    {ajustes ? (
      <div className="mx-1 mb-1 mt-1.5 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-base)] p-2">
        <AllocationPrizeModeFields
          value={{
            prizeMode: row.prizeMode ?? 'weight',
            rating: row.rating ?? null,
            cooldownMinutes: row.cooldownMinutes ?? null,
            hiddenFromGames: !!row.hiddenFromGames,
          }}
          stockRating={stock?.rating ?? null}
          disabled={setPrize.isPending}
          onChange={(patch) => setPrize.mutate(patch)}
        />
      </div>
    ) : null}
    </li>
  );
}
